/* eslint-disable @typescript-eslint/prefer-promise-reject-errors -- validator system uses this weird approach */
import { Form, type FormInstance } from "antd";
import { RequestChannel } from "@vyne-shared/api/VynePayments/responses";
import { type StrictlyBasisPoints, basisPointsFromDollars } from "@vyne-shared/utils/currency";
import { isNotEmptyOrWhiteSpace } from "@vyne-shared/utils/strings";
import { isValidPhone } from "@vyne-shared/utils/phone";
import { useCallback, useMemo } from "react";
import { validateEmail } from "@vyne-shared/utils/email";
import type { PatientUpdateForm } from "api/requests";
import type { Rule, RuleObject } from "antd/es/form";

/** This file is just here to hold static functions and prevent them from cluttering the component file. */

export function balanceCheck(balance?: StrictlyBasisPoints) {
    return (_: RuleObject, value: number | null) => {
        if (!balance) return Promise.resolve();
        if (basisPointsFromDollars(value ?? 0) !== balance)
            return Promise.reject("Amount differs from current balance.");
        return Promise.resolve();
    };
}

function useContactValidator(
    core: (value: string) => boolean,
    initial: string | undefined,
    requiredErrorMessage: string,
    errorMessage: string,
    warningMessage: string,
) {
    return useCallback(
        (rule: RuleObject, value: string) => {
            const [required, warner] = [!!rule.required, !!rule.warningOnly];

            if (!isNotEmptyOrWhiteSpace(value))
                return required ? Promise.reject(requiredErrorMessage) : Promise.resolve();

            if (!warner) {
                // when untouched, skip this instance of the validator, so that the second instance of the validator
                // registered in useDynamicValidation (with warningOnly true) can run.
                const touched = required || getTouched(initial, value);
                return core(value) ? Promise.resolve() : touched ? Promise.reject(errorMessage) : Promise.resolve();
            } else {
                return core(value) ? Promise.resolve() : Promise.reject(warningMessage);
            }
        },
        [core, initial, requiredErrorMessage, errorMessage, warningMessage],
    );
}

/** Determine if current field value has changed from a non-empty initial value. */
function getTouched(initial: string | undefined, current: string | undefined) {
    return !initial || (!!current && initial !== current);
}

/**
 * Enforce that the user enters phone number, email address, or both.
 * @param form The form to add dynamic validation to.
 * @returns Dynamically computed validation rules, based on the static validationRules defined above.
 */
export function useDynamicValidation(
    form: FormInstance<PatientUpdateForm>,
    initialPhone: string | undefined,
    initialEmail: string | undefined,
    initialRequestChannel: RequestChannel,
) {
    // fallback to initialRequestChannel works around antd useWatch returning undefined on the first render
    const requestChannel = Form.useWatch("requestChannel", form) ?? initialRequestChannel;

    const required = {
        SMS: getRequired(RequestChannel.SMS, requestChannel),
        Email: getRequired(RequestChannel.Email, requestChannel),
    } as const;

    const validator = {
        SMS: useContactValidator(
            isValidPhone,
            initialPhone,
            "Required to send Text",
            "Invalid phone",
            "Invalid phone won't be used.",
        ),
        Email: useContactValidator(
            validateEmail,
            initialEmail,
            "Required to send Email",
            "Invalid email address",
            "Invalid email address won't be used.",
        ),
    } as const;

    // Validation rules for the New Payment form.
    // Note that validateFirst is turned on for the phone and email form items, so that we stop on the first
    // validation error per field.
    const mergedValidationRules = useMemo(
        () => ({
            phone: [
                {
                    required: required.SMS,
                    // The phone input uses an different internal value than what is displayed in the UI.
                    // We just make sure they have 11 digits since they cannot enter alpha chars.
                    // internal: +12133734253
                    // display: (213) 337-4253
                    validator: validator.SMS,
                },
                {
                    required: required.SMS,
                    warningOnly: true,
                    validator: validator.SMS,
                },
            ] as const satisfies Rule[],
            email: [
                {
                    required: required.Email,
                    validator: validator.Email,
                },
                {
                    required: required.Email,
                    warningOnly: true,
                    validator: validator.Email,
                },
            ] as const satisfies Rule[],
        }),
        [required.SMS, required.Email, validator.SMS, validator.Email],
    );

    return mergedValidationRules;
}

/** Determine if a valid contact detail for a particular request channel is required. */
function getRequired(requestChannel: RequestChannel, currentRequestChannel: RequestChannel | undefined) {
    return currentRequestChannel === requestChannel || currentRequestChannel === RequestChannel.Both;
}
